import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { fetchReasonTypes, UpdateReasonService } from "../Services/APIService";

interface ReasonData {
  id: number;
  typeId: number;
  description: string;
  isActive: boolean;
}

interface ReasonType {
  id: number;
  name: string;
}

interface UpdateReasonProps {
  viewData: ReasonData | null;
  onUpdateComplete: () => void;
  onCancel: () => void;
}

const UpdateReason: React.FC<UpdateReasonProps> = ({
  viewData,
  onUpdateComplete,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    id: 0,
    description: "",
    isActive: true,
    typeId: 0,
  });

  const [reasonTypes, setReasonTypes] = useState<ReasonType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requiredField = <span className="text-danger ms-1">*</span>;

  // Load reason types
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const data = await fetchReasonTypes();
        setReasonTypes(data);
      } catch (err) {
        console.error(err);
      }
    };

    loadTypes();
  }, []);

  // Bind data when editing
  useEffect(() => {
    if (viewData) {
      setFormData({
        id: viewData.id,
        description: viewData.description,
        isActive: viewData.isActive,
        typeId: viewData.typeId,
      });
    }
  }, [viewData]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "typeId" ? parseInt(value) : value,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      isActive: checked,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.description.trim()) {
      setError("Please enter a valid Reason");
      return;
    }

    try {
      setIsLoading(true);

      const userId = localStorage.getItem("UserId");

      const payload = {
        id: formData.id,
        typeId: formData.typeId,
        description: formData.description.trim(),
        isActive: formData.isActive,
        modifiedBy: parseInt(userId || "0"),
      };

      const result = await UpdateReasonService(payload);

      if (result.isUpdated) {
        toast.success("Reason updated successfully!");
        onUpdateComplete();
      } else {
        toast.error(result.message || "Update failed");
      }
    } catch (err) {
      setError("Error updating reason");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onCancel();
  };

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">Update Reason</h5>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-danger text-danger">{error}</div>
          )}

          <div className="row">

            <div className="col-lg-6 mb-3">
              <label className="form-label">Reason ID</label>
              <input
                type="number"
                className="form-control"
                value={formData.id}
                disabled
              />
            </div>

            <div className="col-lg-6 mb-3">
              <label className="form-label">
                Reason Type {requiredField}
              </label>

              <select
                name="typeId"
                className="form-select"
                value={formData.typeId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Reason Type</option>

                {reasonTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-lg-6 mb-3">
              <label className="form-label">
                Reason Name {requiredField}
              </label>

              <input
                type="text"
                name="description"
                className="form-control"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter Reason"
                required
              />
            </div>

            <div className="col-lg-6 mb-3">
              <label className="form-check mt-4">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={handleCheckboxChange}
                />
                <span className="form-check-label">Active</span>
              </label>
            </div>

            <div className="col-12">
              <button
                className="btn btn-primary me-2"
                disabled={isLoading}
              >
                {isLoading ? "Updating..." : "Update"}
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            </div>

          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateReason;